import { useState } from 'react'
import { ChevronDown, Filter, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { SearchableSelect, type FieldOption } from './SearchableSelect'
import { MultiSearchableSelect } from './MultiSearchableSelect'
import { regionTerminoCap } from '../lib/paisMapa'
import type { CampoExterno, MetadataResponse } from '../types'

export interface DynamicFiltersValue {
  desde?: string
  hasta?: string
  /**
   * Filtros categóricos (provincia, producto, campos dinámicos con opciones) son
   * multi-selección: su valor es un string **coma-separado** de IDs (ej.
   * `"15,16"`). El backend lo expande a parámetros repetidos hacia el upstream
   * (ver `_split_multivalor`). Un solo valor va sin comas. Los booleanos y el
   * rango de fechas siguen siendo de valor único.
   */
  provincia?: string
  /** Claves dinámicas declaradas `filtrable: true` en metadata.campos_externos. */
  [extra: string]: string | undefined
}

export type { FieldOption }

interface Props {
  /** Reporte activo — determina el término geográfico (Provincia vs Departamento). */
  configId?: number
  metadata?: MetadataResponse | null
  value: DynamicFiltersValue
  onChange: (next: DynamicFiltersValue) => void
  /** Campos dinámicos a omitir (ej: si la pestaña ya filtra implícitamente). */
  omitDynamicFields?: string[]
  /** Oculta los inputs Desde/Hasta — usar cuando el rango lo controla un MonthFilter externo. */
  hideDateRange?: boolean
  /**
   * Muestra el select de categoría "Consumo / Farma" (reporte 7). Es un filtro
   * client-side: su valor se guarda en `value.categoria` y NO se envía al upstream.
   */
  showCategoria?: boolean
  /** Si true, el panel empieza expandido. Default: false. */
  defaultOpen?: boolean
  /**
   * Opciones para renderizar un Select en lugar de Input en campos string filtrables.
   * Clave: `campos_externos.key`. Acepta `string[]` (value = label, retrocompat) o
   * `FieldOption[]` (value = ID enviado al API, label = nombre mostrado).
   */
  fieldOptions?: Record<string, string[] | FieldOption[]>
  /**
   * Filtros booleanos extra (Todos/Sí/No) que NO provienen de `campos_externos`.
   * Útil cuando el param de filtro del upstream difiere del campo mostrado en la
   * tabla (ej. Medifarma Perú: columna `email_verificado`, filtro `validado`).
   */
  boolFilters?: Array<{ key: string; label: string }>
  /**
   * Selects de cascada extra (ej. Jefe de Ventas / Representante Médico) que NO
   * se envían al upstream: su selección solo acota las opciones de otro filtro
   * (Representante). Se manejan con value/onChange externos y se renderizan junto
   * a los campos dinámicos. Solo se muestran los que tienen opciones.
   */
  cascadeSelects?: Array<{
    key: string
    label: string
    options: FieldOption[]
    value?: string
    onChange: (v: string | undefined) => void
  }>
  /**
   * Selects client-side de valor único (ej. "Actividad": Todos/Activos/Inactivos)
   * con estado externo. NO se envían al upstream ni forman parte de `value`; solo
   * se renderizan dentro de la grilla junto al resto de filtros.
   */
  extraSelects?: Array<{
    key: string
    label: string
    options: Array<{ value: string; label: string }>
    value: string
    onChange: (v: string) => void
  }>
}

function toOptions(raw: string[] | FieldOption[] | undefined): FieldOption[] {
  if (!raw || raw.length === 0) return []
  if (typeof raw[0] === 'string') {
    return (raw as string[]).map((s) => ({ value: s, label: s }))
  }
  return raw as FieldOption[]
}

export function DynamicFiltersBar({
  configId = 0,
  metadata,
  value,
  onChange,
  omitDynamicFields = [],
  hideDateRange = false,
  showCategoria = false,
  defaultOpen = false,
  fieldOptions,
  boolFilters,
  cascadeSelects,
  extraSelects,
}: Props) {
  const [open, setOpen] = useState(defaultOpen)

  const set = (key: keyof DynamicFiltersValue | string, v: string | undefined) => {
    const next = { ...value }
    if (!v) delete next[key as string]
    else (next as Record<string, string>)[key as string] = v
    onChange(next)
  }

  const camposFiltrables: CampoExterno[] =
    metadata?.campos_externos?.filter(
      (c) => c.filtrable && !omitDynamicFields.includes(c.key),
    ) ?? []

  const hasAny =
    (!hideDateRange && (Boolean(value.desde) || Boolean(value.hasta))) ||
    Boolean(value.provincia) ||
    Object.entries(value).some(
      ([k, v]) => v && !['desde', 'hasta', 'provincia'].includes(k),
    )

  return (
    <div className="rounded-lg border border-gray-300 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <Filter className="h-3.5 w-3.5" />
          Filtros
          {hasAny && (
            <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 text-[10px] font-semibold">
              •
            </span>
          )}
        </span>
        <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {open && <div className="px-4 pb-4 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {!hideDateRange && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="f-desde" className="text-xs">Desde</Label>
              <Input
                id="f-desde"
                type="date"
                value={value.desde ?? ''}
                onChange={(e) => set('desde', e.target.value || undefined)}
                max={value.hasta}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-hasta" className="text-xs">Hasta</Label>
              <Input
                id="f-hasta"
                type="date"
                value={value.hasta ?? ''}
                onChange={(e) => set('hasta', e.target.value || undefined)}
                min={value.desde}
              />
            </div>
          </>
        )}

        {(() => {
          // Preferimos las opciones provistas por el padre (pares ID/nombre
          // sacados del listado de participantes) sobre `metadata.provincias_con_datos`,
          // que en grupofarma devuelve IDs y nombres mezclados.
          const provinciaOpts = toOptions(
            fieldOptions?.provincia ?? metadata?.provincias_con_datos,
          )
          if (provinciaOpts.length === 0) return null
          return (
            <div className="space-y-1.5">
              <Label htmlFor="f-provincia" className="text-xs">{regionTerminoCap(configId)}</Label>
              <MultiSearchableSelect
                id="f-provincia"
                placeholder="Todas"
                options={provinciaOpts}
                value={value.provincia}
                onChange={(v) => set('provincia', v)}
              />
            </div>
          )
        })()}

        {(() => {
          // Producto: select buscable cuando el padre provee opciones.
          const productoOpts = toOptions(fieldOptions?.producto)
          if (productoOpts.length === 0) return null
          return (
            <div className="space-y-1.5">
              <Label htmlFor="f-producto" className="text-xs">Producto</Label>
              <MultiSearchableSelect
                id="f-producto"
                placeholder="Todos"
                options={productoOpts}
                value={value.producto}
                onChange={(v) => set('producto', v)}
              />
            </div>
          )
        })()}

        {showCategoria && (
          <div className="space-y-1.5">
            <Label htmlFor="f-categoria" className="text-xs">Consumo / Farma</Label>
            <Select
              value={value.categoria ?? '__all__'}
              onValueChange={(v) => set('categoria', v === '__all__' ? undefined : v)}
            >
              <SelectTrigger id="f-categoria">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="Consumo">Consumo</SelectItem>
                <SelectItem value="Farma">Farma</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {(() => {
          // Dependiente: participante registrado (single-select buscable). El value
          // es el `id` que el upstream espera en `participante_id`. Se renderiza
          // solo cuando el padre provee opciones (reporte 7).
          const dependienteOpts = toOptions(fieldOptions?.participante_id)
          if (dependienteOpts.length === 0) return null
          return (
            <div className="space-y-1.5">
              <Label htmlFor="f-participante_id" className="text-xs">Dependiente</Label>
              <SearchableSelect
                id="f-participante_id"
                placeholder="Todos"
                options={dependienteOpts}
                value={value.participante_id}
                onChange={(v) => set('participante_id', v)}
              />
            </div>
          )
        })()}

        {(cascadeSelects ?? [])
          .filter((s) => s.options.length > 0)
          .map((s) => (
            <div key={s.key} className="space-y-1.5">
              <Label htmlFor={`f-${s.key}`} className="text-xs">{s.label}</Label>
              <MultiSearchableSelect
                id={`f-${s.key}`}
                placeholder="Todos"
                options={s.options}
                value={s.value}
                onChange={s.onChange}
              />
            </div>
          ))}

        {camposFiltrables.map((c) => {
          if (c.tipo === 'bool') {
            return (
              <div key={c.key} className="space-y-1.5">
                <Label htmlFor={`f-${c.key}`} className="text-xs">{c.label}</Label>
                <Select
                  value={value[c.key] ?? '__all__'}
                  onValueChange={(v) => set(c.key, v === '__all__' ? undefined : v)}
                >
                  <SelectTrigger id={`f-${c.key}`}>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    <SelectItem value="true">Sí</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )
          }
          // Si el padre nos entrega opciones para esta key, mostramos un combobox
          // buscable independientemente del `tipo` declarado en metadata.
          const opciones = toOptions(fieldOptions?.[c.key])
          if (opciones.length > 0 && c.tipo !== 'number' && c.tipo !== 'date') {
            return (
              <div key={c.key} className="space-y-1.5">
                <Label htmlFor={`f-${c.key}`} className="text-xs">{c.label}</Label>
                <MultiSearchableSelect
                  id={`f-${c.key}`}
                  placeholder="Todos"
                  options={opciones}
                  value={value[c.key]}
                  onChange={(v) => set(c.key, v)}
                />
              </div>
            )
          }
          return (
            <div key={c.key} className="space-y-1.5">
              <Label htmlFor={`f-${c.key}`} className="text-xs">{c.label}</Label>
              <Input
                id={`f-${c.key}`}
                type={c.tipo === 'number' ? 'number' : c.tipo === 'date' ? 'date' : 'text'}
                value={value[c.key] ?? ''}
                onChange={(e) => set(c.key, e.target.value || undefined)}
                placeholder={c.label}
              />
            </div>
          )
        })}

        {(boolFilters ?? []).map((b) => (
          <div key={b.key} className="space-y-1.5">
            <Label htmlFor={`f-${b.key}`} className="text-xs">{b.label}</Label>
            <Select
              value={value[b.key] ?? '__all__'}
              onValueChange={(v) => set(b.key, v === '__all__' ? undefined : v)}
            >
              <SelectTrigger id={`f-${b.key}`}>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="true">Sí</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}

        {(extraSelects ?? []).map((s) => (
          <div key={s.key} className="space-y-1.5">
            <Label htmlFor={`f-${s.key}`} className="text-xs">{s.label}</Label>
            <Select value={s.value} onValueChange={s.onChange}>
              <SelectTrigger id={`f-${s.key}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {s.options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      {hasAny && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange({})}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="mr-1 h-3 w-3" />
            Limpiar filtros
          </Button>
        </div>
      )}
      </div>}
    </div>
  )
}
